import * as React from 'react';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// SegmentedProgressBar — generic, render-only component
// ---------------------------------------------------------------------------

/** A single segment in the stacked progress bar. */
export interface SegmentDef {
  /** Unique React key */
  key: string;
  /** Width as a percentage of the total bar (0–100). Zero-width segments are skipped. */
  widthPct: number;
  /** Tailwind class(es) applied to the segment div (background color, etc.). */
  colorClass: string;
  /**
   * Label shown in the hover tooltip when showLegend is true.
   * Omit to exclude this segment from tooltips.
   */
  label?: string;
}

export type BudgetProgressSegmentKey =
  | 'direct'
  | 'children'
  | 'allocated'
  | 'unallocated'
  | 'over'
  | 'spent';

interface SegmentedProgressBarProps {
  /** Segments rendered left-to-right. Their widthPct values need not sum to 100. */
  segments: SegmentDef[];
  /** Current value (0–100) used for the aria-valuenow attribute. */
  valuePct: number;
  /** Height of the bar track. Defaults to 'md'. */
  size?: 'sm' | 'md' | 'lg';
  /** When true, hovering over a segment shows a tooltip with its label. */
  showLegend?: boolean;
  /** Called when a labeled segment is clicked. */
  onSegmentClick?: (key: string) => void;
  className?: string;
}

const trackHeightClass: Record<NonNullable<SegmentedProgressBarProps['size']>, string> = {
  sm: 'h-1.5',
  md: 'h-3',
  lg: 'h-5',
};

export function SegmentedProgressBar({
  segments,
  valuePct,
  size = 'md',
  showLegend = false,
  onSegmentClick,
  className,
}: SegmentedProgressBarProps) {
  const visibleSegments = segments.filter((s) => s.widthPct > 0);

  return (
    <div className={cn('w-full', className)}>
      {/*
       * Outer wrapper is `relative` so the invisible tooltip-trigger overlay can
       * be stacked above the track using `absolute inset-0`. The track itself keeps
       * `overflow-hidden` for its rounded corners without clipping the tooltips,
       * because the tooltips live in the sibling overlay div.
       */}
      <div className={cn('relative w-full', showLegend && 'pb-8')}>
        {/* Visible colored track */}
        <div
          className={cn(
            'w-full rounded-full bg-muted overflow-hidden',
            trackHeightClass[size]
          )}
          role="progressbar"
          aria-valuenow={Math.round(valuePct)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="h-full flex">
            {visibleSegments.map((seg) => (
              <div
                key={seg.key}
                className={cn('h-full', seg.colorClass)}
                style={{ width: `${seg.widthPct}%` }}
              />
            ))}
          </div>
        </div>

        {/* Invisible hover regions — absolute overlay, same widths as segments */}
        {showLegend && (
          <div className="absolute inset-0 flex overflow-visible">
            {visibleSegments.map((seg, index) => {
              if (!seg.label) {
                return (
                  <div key={seg.key} className="h-full" style={{ width: `${seg.widthPct}%` }} />
                );
              }

              const startPct = visibleSegments
                .slice(0, index)
                .reduce((sum, s) => sum + s.widthPct, 0);
              const endPct = startPct + seg.widthPct;
              // Keep edge tooltips inside the bar instead of centering off-screen
              const align: 'start' | 'center' | 'end' =
                index === 0 || startPct < 12
                  ? 'start'
                  : index === visibleSegments.length - 1 || endPct > 88
                    ? 'end'
                    : 'center';

              return (
                <button
                  key={seg.key}
                  type="button"
                  className={cn(
                    'group/seg relative h-full overflow-visible border-0 bg-transparent p-0',
                    onSegmentClick ? 'cursor-pointer' : 'cursor-default'
                  )}
                  style={{ width: `${seg.widthPct}%` }}
                  aria-label={seg.label}
                  onClick={() => onSegmentClick?.(seg.key)}
                >
                  <div
                    className={cn(
                      'pointer-events-none absolute top-full z-10 mt-1.5',
                      'whitespace-nowrap rounded-md border border-border px-2 py-1',
                      'bg-popover text-popover-foreground shadow-md text-xs',
                      'opacity-0 scale-95 transition-all duration-150',
                      'group-hover/seg:opacity-100 group-hover/seg:scale-100',
                      align === 'start' && 'left-0 origin-top-left',
                      align === 'center' && 'left-1/2 -translate-x-1/2 origin-top',
                      align === 'end' && 'right-0 origin-top-right'
                    )}
                  >
                    <span
                      className={cn(
                        'absolute bottom-full border-4 border-transparent border-b-popover',
                        align === 'start' && 'left-3',
                        align === 'center' && 'left-1/2 -translate-x-1/2',
                        align === 'end' && 'right-3'
                      )}
                    />
                    <span className="flex items-center gap-1.5">
                      <span className={cn('inline-block h-2 w-2 shrink-0 rounded-sm', seg.colorClass)} />
                      {seg.label}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProgressBar — simple single-value bar (kept for backward compatibility)
// ---------------------------------------------------------------------------

interface ProgressBarProps {
  value: number;
  max?: number;
  variant?: 'default' | 'success' | 'warning' | 'destructive';
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  className?: string;
}

export function ProgressBar({
  value,
  max = 100,
  variant = 'default',
  size = 'md',
  showLabel = false,
  className,
}: ProgressBarProps) {
  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  const variantColorClass: Record<NonNullable<ProgressBarProps['variant']>, string> = {
    default: 'bg-progress-spent',
    success: 'bg-progress-children',
    warning: 'bg-progress-unallocated',
    destructive: 'bg-progress-over',
  };

  const autoVariant = React.useMemo(() => {
    if (variant !== 'default') return variant;
    if (percentage > 100) return 'destructive';
    if (percentage >= 90) return 'warning';
    return 'default';
  }, [percentage, variant]);

  return (
    <div className={cn('w-full', className)}>
      <SegmentedProgressBar
        segments={[
          {
            key: 'value',
            widthPct: percentage,
            colorClass: cn(
              'rounded-full transition-all duration-300 ease-out',
              variantColorClass[autoVariant]
            ),
          },
        ]}
        valuePct={percentage}
        size={size}
      />
      {showLabel && (
        <div className="mt-1 flex justify-between text-xs text-muted-foreground">
          <span>{value.toFixed(0)}</span>
          <span>{max.toFixed(0)}</span>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BudgetProgress — intermediate component: calculations → SegmentedProgressBar
// ---------------------------------------------------------------------------

interface BudgetProgressProps {
  spent: number;
  directSpent?: number;
  allocated?: number;
  budget: number;
  currency: string;
  secondaryCurrency?: string;
  secondarySpent?: number;
  secondaryRemaining?: number;
  secondaryBudget?: number;
  percentSpent?: number;
  remaining?: number;
  unallocated?: number;
  rolloverAmount?: number;
  onRolloverClick?: () => void;
  onSegmentClick?: (key: BudgetProgressSegmentKey) => void;
  className?: string;
}

export function BudgetProgress({
  spent,
  directSpent,
  allocated,
  budget,
  currency,
  secondaryCurrency,
  secondarySpent,
  secondaryRemaining,
  secondaryBudget: _secondaryBudget,
  percentSpent,
  remaining,
  unallocated,
  rolloverAmount,
  onRolloverClick,
  onSegmentClick,
  className,
}: BudgetProgressProps) {
  const percentage = percentSpent ?? (budget > 0 ? (spent / budget) * 100 : 0);
  const remainingAmount = remaining ?? (budget - spent);
  const isOver = remainingAmount < 0;
  const isCompleted = remainingAmount === 0;
  const hasChildSpend = directSpent !== undefined && directSpent < spent;

  const formatAmount = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  const formatSecondaryAmount = (amount: number | undefined) =>
    secondaryCurrency && amount !== undefined
      ? ` (${new Intl.NumberFormat('en-US', { style: 'currency', currency: secondaryCurrency }).format(amount)})`
      : '';

  // ------------------------------------------------------------------
  // Compute segments (each widthPct is relative to the total budget)
  // ------------------------------------------------------------------
  const total = budget > 0 ? budget : 1;
  const hasSegments = hasChildSpend || allocated !== undefined;

  const directSpentAmt = directSpent ?? (hasChildSpend ? 0 : spent);
  const childSpentAmt = hasChildSpend ? Math.max(0, spent - directSpentAmt) : 0;
  const showUnallocated = unallocated !== undefined || allocated !== undefined;
  const displayUnallocated = showUnallocated
    ? Math.max(0, budget - (allocated ?? 0) - directSpentAmt)
    : undefined;
  const unspentAllocatedAmt =
    allocated !== undefined ? Math.max(0, allocated - childSpentAmt) : 0;

  const directSpentPct = Math.min(100, (directSpentAmt / total) * 100);
  const childSpentPct = Math.min(100 - directSpentPct, (childSpentAmt / total) * 100);
  const unspentAllocatedPct = Math.min(
    100 - directSpentPct - childSpentPct,
    (unspentAllocatedAmt / total) * 100
  );
  const unallocatedPct =
    displayUnallocated !== undefined
      ? Math.min(
          100 - directSpentPct - childSpentPct - unspentAllocatedPct,
          (displayUnallocated / total) * 100
        )
      : 0;

  // Build the segments array passed to the generic bar
  const segments: SegmentDef[] = isOver
    ? [
        {
          key: 'over',
          widthPct: 100,
          colorClass: 'bg-progress-over',
          label: `Over budget by ${formatAmount(Math.abs(remainingAmount))}`,
        },
      ]
    : hasSegments
      ? [
          {
            key: 'direct',
            widthPct: directSpentPct,
            colorClass: 'bg-progress-direct',
            label: directSpentPct > 0 ? `Direct (${formatAmount(directSpentAmt)})` : undefined,
          },
          {
            key: 'children',
            widthPct: childSpentPct,
            colorClass: 'bg-progress-unallocated',
            label: childSpentPct > 0 ? `Children (${formatAmount(childSpentAmt)})` : undefined,
          },
          {
            key: 'allocated',
            widthPct: unspentAllocatedPct,
            colorClass: 'bg-progress-allocated',
            label:
              unspentAllocatedPct > 0
                ? `Allocated unspent (${formatAmount(unspentAllocatedAmt)})`
                : undefined,
          },
          {
            key: 'unallocated',
            widthPct: unallocatedPct,
            colorClass: 'bg-progress-children',
            label:
              displayUnallocated !== undefined && displayUnallocated > 0
                ? `Unallocated (${formatAmount(displayUnallocated)})`
                : undefined,
          },
        ]
      : [
          {
            key: 'spent',
            widthPct: Math.min(100, percentage),
            colorClass: cn(
              'rounded-full transition-all duration-300 ease-out',
              isCompleted ? 'bg-progress-children' : 'bg-progress-spent'
            ),
          },
        ];

  const remainingLabel = isOver ? (
    <>
      {formatAmount(Math.abs(remainingAmount))}
      {formatSecondaryAmount(
        secondaryRemaining !== undefined ? Math.abs(secondaryRemaining) : undefined
      )}{' '}
      over
    </>
  ) : isCompleted ? (
    <>Completed</>
  ) : (
    <>
      {formatAmount(remainingAmount)}
      {formatSecondaryAmount(secondaryRemaining)} left
    </>
  );

  return (
    <div className={cn('space-y-1.5', className)}>
      {/* Option A: single meter line — spent · remaining */}
      <div className="flex items-center justify-between gap-2 text-sm min-w-0">
        <p className="min-w-0 truncate text-muted-foreground">
          {formatAmount(spent)}
          {formatSecondaryAmount(secondarySpent)} spent
          {hasChildSpend && (
            <span className="ml-1 text-xs">({formatAmount(directSpentAmt)} direct)</span>
          )}
          <span className="mx-1.5 text-muted-foreground/70">·</span>
          <span
            className={cn(
              'font-bold',
              isCompleted ? 'text-success' : isOver ? 'text-destructive' : 'text-foreground'
            )}
          >
            {remainingLabel}
          </span>
        </p>
        {(rolloverAmount ?? 0) > 0 && (
          <button
            type="button"
            onClick={onRolloverClick}
            className="shrink-0 text-xs text-success font-medium hover:underline"
          >
            {`+ ${formatAmount(rolloverAmount!)} rollover`}
          </button>
        )}
      </div>

      <SegmentedProgressBar
        segments={segments}
        valuePct={percentage}
        size="lg"
        showLegend={hasSegments}
        onSegmentClick={
          onSegmentClick
            ? (key) => onSegmentClick(key as BudgetProgressSegmentKey)
            : undefined
        }
      />
    </div>
  );
}
